/**
 * Pure helpers for project-location map image captures in the PDF report.
 *
 * The PDF uses `domtoimage.toPng` on the Leaflet `Map` component. If tiles
 * haven't finished loading, `domtoimage` produces a solid grey placeholder
 * (Leaflet's default `.leaflet-container { background: #ddd }`) plus the
 * blue marker. That grey image would otherwise be persisted and displayed
 * in the PDF — these helpers detect that case so the capture pipeline can
 * retry with a proper map.
 */

const LEAFLET_DEFAULT_GREY = { r: 221, g: 221, b: 221 }; // #dddddd from Leaflet CSS
const MANHATTAN_THRESHOLD = 30;
const GRID_SIZE = 10; // 10x10 = 100 sample points
const GREY_MAJORITY_THRESHOLD = 95; // out of 100

/**
 * Decode a map-capture data URL and report whether at least
 * `GREY_MAJORITY_THRESHOLD` of 100 sampled pixels are within
 * `MANHATTAN_THRESHOLD` RGB distance of Leaflet's default grey background.
 *
 * Returns `false` for invalid/empty inputs so callers don't have to special-case them.
 */
export const isMapImageGrey = async (dataUrl: string): Promise<boolean> => {
    if (!dataUrl || !dataUrl.startsWith('data:image/')) return false;

    const img = new Image();
    const loaded = new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('Image decode failed'));
    });
    img.src = dataUrl;
    await loaded;

    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return false;
    ctx.drawImage(img, 0, 0);

    let greyCount = 0;
    for (let xi = 0; xi < GRID_SIZE; xi++) {
        for (let yi = 0; yi < GRID_SIZE; yi++) {
            const px = Math.floor(((xi + 0.5) * canvas.width) / GRID_SIZE);
            const py = Math.floor(((yi + 0.5) * canvas.height) / GRID_SIZE);
            const pixel = ctx.getImageData(px, py, 1, 1).data;
            const [r, g, b] = [pixel[0], pixel[1], pixel[2]];
            const manhattan = Math.abs(r - LEAFLET_DEFAULT_GREY.r) + Math.abs(g - LEAFLET_DEFAULT_GREY.g) + Math.abs(b - LEAFLET_DEFAULT_GREY.b);
            if (manhattan < MANHATTAN_THRESHOLD) {
                greyCount += 1;
            }
        }
    }

    return greyCount >= GREY_MAJORITY_THRESHOLD;
};
